import { useCallback } from "react";

import { ROOT_SPACE_ID } from "constants/space";
import useTypedContext from "hooks/useTypedContext";
import { areRunLogsDeletable } from "shared/Run/useDeleteRunLogs/accessValidation";
import { Run } from "types/generated";
import { AccessResult, hasAdminAccess, NO_ACCESS_MESSAGE } from "utils/access";
import { AccountContext } from "views/AccountWrapper";

/**
 * Hook to determine if the current user can permanently delete a run's logs.
 *
 * Deletion is account-wide rather than stack-scoped, so it is restricted to root space
 * admins, and the backend only accepts it once the run has reached a terminal state.
 *
 * @returns A function that takes a run and returns an AccessResult
 */
const useCanDeleteRunLogs = (): ((run: Run) => AccessResult) => {
  const { viewer } = useTypedContext(AccountContext);

  return useCallback(
    (run: Run) => {
      if (!areRunLogsDeletable(run)) {
        return { result: false };
      }

      if (hasAdminAccess(viewer, ROOT_SPACE_ID)) {
        return { result: true };
      }

      return { result: false, message: NO_ACCESS_MESSAGE };
    },
    [viewer]
  );
};

export default useCanDeleteRunLogs;
